import { ClassValue } from "clsx";
import type { ComponentProps, ElementType, ReactNode } from "react";
import { cn } from "../../utils";

const tailwindFontSizeMap = {
  xxs: "text-xxs",
  xs: "text-xs",
  sm: "text-sm",
  md: "text-md",
  lg: "text-lg",
  xl: "text-xl",
  "2xl": "text-2xl",
  "3xl": "text-3xl",
  "4xl": "text-4xl",
};

export type TailwindFontSize = keyof typeof tailwindFontSizeMap;

export const getTailwindFontSize = (size: TailwindFontSize) =>
  tailwindFontSizeMap[size];

const tailwindFontWeightMap = {
  normal: "font-normal",
  medium: "font-medium",
};

export type TailwindFontWeight = keyof typeof tailwindFontWeightMap;

export const getTailwindFontWeight = (weight: TailwindFontWeight) =>
  tailwindFontWeightMap[weight];

export type TypographyProps<T extends ElementType = ElementType> =
  ComponentProps<T> & {
    as?: T;
    weight?: TailwindFontWeight;
    children: ReactNode;
    className?: ClassValue;
  };

type TypographyBaseProps<T extends ElementType = ElementType> =
  TypographyProps<T> & {
    as: T;
    size: TailwindFontSize;
    weight: TailwindFontWeight;
  };

export const TypographyBase = <T extends ElementType = ElementType>({
  as,
  size,
  weight,
  className,
  children,
  ...elementProps
}: TypographyBaseProps<T>) => {
  const C = as;

  return (
    <C
      {...elementProps}
      className={cn(
        getTailwindFontSize(size),
        getTailwindFontWeight(weight),
        className,
      )}
    >
      {children}
    </C>
  );
};
